import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Warning } from "@phosphor-icons/react";

const empty = { name: "", unit: "kg", stock: 0, min_stock: 0, cost_per_unit: 0, vendor: "" };

export default function Inventory() {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/inventory").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEdit(it);
    setForm({ name: it.name, unit: it.unit, stock: it.stock, min_stock: it.min_stock, cost_per_unit: it.cost_per_unit, vendor: it.vendor || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (edit) await api.put(`/inventory/${edit.id}`, form);
      else await api.post("/inventory", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const adjust = async (id, delta) => {
    try {
      await api.post(`/inventory/${id}/adjust`, { delta, reason: "manual" });
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete inventory item?")) return;
    await api.delete(`/inventory/${id}`); load();
  };

  const lowCount = items.filter((it) => it.stock <= it.min_stock).length;

  return (
    <>
      <PageHeader
        eyebrow="Stock"
        title="Inventory"
        subtitle="Track raw materials, restock levels, vendors and unit costs. Low-stock items light up in red."
        actions={canEdit && <Button onClick={openCreate} className="btn-primary" data-testid="inventory-add-btn"><Plus size={16} weight="bold"/> Add item</Button>}
      />

      {lowCount > 0 && (
        <div className="bento p-4 flex items-center gap-3 mb-6 border-[var(--brand)]/40" data-testid="low-stock-banner">
          <div className="w-10 h-10 rounded-md bg-[var(--brand)]/15 text-[var(--brand)] grid place-items-center">
            <Warning size={20} weight="duotone"/>
          </div>
          <div>
            <div className="font-display font-bold">{lowCount} item{lowCount > 1 ? "s" : ""} below threshold</div>
            <div className="text-xs text-[var(--text-secondary)]">Reorder soon to avoid menu disruptions.</div>
          </div>
        </div>
      )}

      <div className="bento overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 label-eyebrow border-b border-[#EAE7E0] bg-[var(--bg-secondary)]/50">
          <div className="col-span-3">Item</div>
          <div className="col-span-2">Vendor</div>
          <div className="col-span-2">Stock</div>
          <div className="col-span-2">Min</div>
          <div className="col-span-2">Cost / unit</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className="divide-y divide-[#EAE7E0]">
          {items.map((it) => {
            const low = it.stock <= it.min_stock;
            return (
              <div key={it.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center" data-testid={`inv-row-${it.id}`}>
                <div className="col-span-3">
                  <div className="font-semibold text-sm">{it.name}</div>
                  <div className="text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">{it.unit}</div>
                </div>
                <div className="col-span-2 text-sm">{it.vendor || "—"}</div>
                <div className="col-span-2">
                  <div className="flex items-center gap-1">
                    {canEdit && <button onClick={() => adjust(it.id, -1)} className="px-2 py-0.5 rounded bg-[var(--bg-secondary)] text-xs font-bold" data-testid={`inv-dec-${it.id}`}>−</button>}
                    <div className={`font-display font-bold text-sm ${low ? "text-[var(--brand)]" : ""}`}>{it.stock}</div>
                    {canEdit && <button onClick={() => adjust(it.id, 1)} className="px-2 py-0.5 rounded bg-[var(--bg-secondary)] text-xs font-bold" data-testid={`inv-inc-${it.id}`}>+</button>}
                  </div>
                </div>
                <div className="col-span-2 text-sm">{it.min_stock}</div>
                <div className="col-span-2 text-sm font-mono">{fmtMoney(it.cost_per_unit)}</div>
                <div className="col-span-1 flex justify-end gap-1">
                  {canEdit && <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-[var(--bg-secondary)]" data-testid={`inv-edit-${it.id}`}><PencilSimple size={14}/></button>}
                  {canEdit && <button onClick={() => del(it.id)} className="p-1.5 rounded text-[var(--brand)] hover:bg-[var(--bg-secondary)]" data-testid={`inv-del-${it.id}`}><Trash size={14}/></button>}
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-[var(--text-secondary)]">No inventory yet.</div>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inventory-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit item" : "Add item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="inv-form-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" data-testid="inv-form-unit"/>
              </div>
              <div>
                <Label className="label-eyebrow">Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1" data-testid="inv-form-vendor"/>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-eyebrow">Stock</Label>
                <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="inv-form-stock"/>
              </div>
              <div>
                <Label className="label-eyebrow">Min</Label>
                <Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="inv-form-min"/>
              </div>
              <div>
                <Label className="label-eyebrow">Cost/unit</Label>
                <Input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="inv-form-cost"/>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="btn-primary" data-testid="inv-form-save">{edit ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
